/**
 * PodReceiptPage - Public, shareable Proof of Delivery receipt.
 *
 * Accessed via /pod/:orderId (no auth required).
 * Fetches the capability-protected receipt + media blobs and renders the shared
 * PodReceiptCard. The same card is reused in the desktop app (DesktopReceiptModal)
 * — this page owns only the public fetch/token/media wiring for the web.
 */
import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Printer, X, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

import { usePublicReceipt } from '@/hooks/usePublicReceipt';
import PodReceiptCard from '@/components/delivery/PodReceiptCard';

export default function PodReceiptPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const token = new URLSearchParams(location.hash.slice(1)).get('token') || '';
  // Router identity retires A→B→A openings as well as same-order token changes.
  return <ReceiptOpening key={JSON.stringify([location.key, orderId, token])} orderId={orderId} token={token} />;
}

function ReceiptOpening({ orderId, token }) {
  const [attempt, setAttempt] = useState(0);
  return <ReceiptAttempt key={attempt} orderId={orderId} token={token} onRetry={() => setAttempt(value => value + 1)} />;
}

function ReceiptAttempt({ orderId, token, onRetry }) {
  const navigate = useNavigate();
  const { state, data, error, media, retryMedia } = usePublicReceipt(orderId, token);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const mediaUrls = Object.fromEntries(Object.entries(media).filter(([key, item]) => key !== 'arrangement' && item.url)
    .map(([key, item]) => [key, item.url]));
  const arrangementUrl = media.arrangement?.url || null;
  const incomplete = Object.values(media).filter(item => item.state !== 'available');

  // This page has no nav chrome of its own and is often opened in a chromeless
  // mobile webview (no browser back/tabs), so it needs its own way out.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading receipt...</span>
        </div>
      </div>
    );
  }

  if (state === 'unavailable') {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 max-w-md text-center" data-testid="pod-receipt-error">
          <AlertCircle className="h-10 w-10 text-stone-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-stone-900 mb-1">Receipt Not Available</h1>
          <p className="text-sm text-stone-500">{error || 'Receipt not available.'}</p>
          {token && <button onClick={onRetry} className="mt-4 underline" data-testid="pod-receipt-retry">Retry receipt</button>}
          <button
            onClick={handleBack}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors shadow-sm"
            data-testid="pod-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 py-6 px-4 print:bg-white print:py-0 print:px-0">
      <div className="max-w-2xl mx-auto">
        {/* Top controls - hidden when printing */}
        <div className="flex justify-between mb-3 print:hidden">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors shadow-sm"
            data-testid="pod-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors shadow-sm"
            data-testid="pod-print-btn"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>

        {incomplete.length > 0 && (
          <div className="mb-3 rounded-lg border border-stone-200 bg-white p-3 text-sm" role="status" data-testid="pod-media-status">
            {incomplete.map(item => <p key={item.endpoint}>{item.label}: {item.state === 'loading' ? 'Loading' : 'Unavailable — could not load recorded media'}</p>)}
            {incomplete.some(item => item.state === 'unavailable') && (
              <button onClick={retryMedia} className="mt-2 underline print:hidden" data-testid="pod-media-retry">Retry unavailable media</button>
            )}
          </div>
        )}
        <PodReceiptCard
          data={data}
          orderId={orderId}
          mediaUrls={mediaUrls}
          arrangementUrl={arrangementUrl}
          onImageClick={setLightboxSrc}
        />
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 print:hidden"
          onClick={() => setLightboxSrc(null)}
          data-testid="pod-receipt-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-stone-300"
            onClick={() => setLightboxSrc(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxSrc}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
